/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ParticipantScheduleRow` for the Caintra Connect frontend.
 * @module components/B2BEventParticipatingCompanyForm/ParticipantScheduleRow
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { ReactComponent as DeleteIcon } from "../../assets/icons/B2BEvents/DeleteIcon.svg";
import {
  getEmptyScheduleValidation,
  getScheduleValidation,
  toHHmm,
} from "../../utils/participantScheduleValidation.js";

const ParticipantScheduleRow = ({
  userId,
  scheduleIndex,
  schedule,
  entry,
  schedulesCount,
  eventDateStart,
  eventDateEnd,
  eventTimeStart,
  eventTimeEnd,
  onScheduleChange,
  onRemoveSchedule,
  styles,
  t,
}) => {
  const validation = entry
    ? getScheduleValidation(entry, {
        eventDateStart,
        eventDateEnd,
        eventTimeStart,
        eventTimeEnd,
      })
    : getEmptyScheduleValidation();

  return (
    <div
      key={`${userId}-${scheduleIndex}`}
      className={`${styles.formRow} ${styles.participantUserEditRow}`}
    >
      <div className={styles.participantUserEditRowInputs}>
        <div className={styles.participantUserEditCol}>
          <label className={styles.label}>
            {t("pages.b2bEventForm.participantUserDate")}
          </label>
          <input
            type="date"
            className={`${styles.input} ${validation.fecha ? styles.inputError : ""}`}
            value={schedule.fecha}
            min={eventDateStart ?? undefined}
            max={eventDateEnd ?? undefined}
            onChange={(e) =>
              onScheduleChange(userId, scheduleIndex, { fecha: e.target.value })
            }
          />
        </div>
        <div className={styles.participantUserEditCol}>
          <label className={styles.label}>
            {t("pages.b2bEventForm.participantUserStartTime")}
          </label>
          <input
            type="time"
            className={`${styles.input} ${validation.horaInicio ? styles.inputError : ""}`}
            value={toHHmm(schedule.horaInicio) || ""}
            onChange={(e) =>
              onScheduleChange(userId, scheduleIndex, {
                horaInicio: e.target.value,
              })
            }
          />
        </div>
        <div className={styles.participantUserEditCol}>
          <label className={styles.label}>
            {t("pages.b2bEventForm.participantUserEndTime")}
          </label>
          <input
            type="time"
            className={`${styles.input} ${validation.horaFin ? styles.inputError : ""}`}
            value={toHHmm(schedule.horaFin) || ""}
            onChange={(e) =>
              onScheduleChange(userId, scheduleIndex, { horaFin: e.target.value })
            }
          />
        </div>
        <div className={styles.participantUserEditCol}>
          <label className={styles.label}>
            {t("pages.b2bEventForm.participantUserLunchStart")}
          </label>
          <input
            type="time"
            className={`${styles.input} ${validation.horaInicioComida ? styles.inputError : ""}`}
            value={toHHmm(schedule.horaInicioComida) || ""}
            onChange={(e) =>
              onScheduleChange(userId, scheduleIndex, {
                horaInicioComida: e.target.value,
              })
            }
          />
        </div>
        <div className={styles.participantUserEditCol}>
          <label className={styles.label}>
            {t("pages.b2bEventForm.participantUserLunchEnd")}
          </label>
          <input
            type="time"
            className={`${styles.input} ${validation.horaFinComida ? styles.inputError : ""}`}
            value={toHHmm(schedule.horaFinComida) || ""}
            onChange={(e) =>
              onScheduleChange(userId, scheduleIndex, {
                horaFinComida: e.target.value,
              })
            }
          />
        </div>
      </div>
      <div className={styles.deleteDateRowButtonWrapper}>
        <button
          type="button"
          className={styles.deleteDateRowButton}
          disabled={schedulesCount <= 1}
          onClick={() => onRemoveSchedule(userId, scheduleIndex)}
          title={
            schedulesCount <= 1
              ? t("pages.b2bEventForm.cannotRemoveOnlyDate")
              : undefined
          }
        >
          <DeleteIcon />
        </button>
      </div>
    </div>
  );
};

export default ParticipantScheduleRow;
